import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import type { PaymentReminderScope } from '@residenone/shared';
import { getRemindersPreview, sendPaymentReminders, type PaymentRemindersPreview } from '../../lib/reminders-api';
import { extractApiError } from '../../lib/api-client';
import { formatRupiah, formatDate, formatDateTime } from '../../lib/format';
import { useT } from '../../i18n';

const DEFAULT_MESSAGE =
  'Halo, ini pengingat untuk pembayaran iuran bulanan (IPL). Mohon segera diselesaikan. Terima kasih.';

export function PaymentRemindersPage() {
  const t = useT();
  const [scope, setScope] = useState<PaymentReminderScope>('owing');
  const [data, setData] = useState<PaymentRemindersPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async (s: PaymentReminderScope) => {
    setLoading(true); setError(null);
    try { const d = await getRemindersPreview(s); if (mountedRef.current) setData(d); }
    catch (e) { if (mountedRef.current) setError(extractApiError(e).message); }
    finally { if (mountedRef.current) setLoading(false); }
  }, []);
  useEffect(() => { load(scope); }, [scope, load]);

  const recipients = data?.recipients ?? [];
  const sendable = recipients.filter((rr) => rr.main_user_id !== null);
  const plural = sendable.length === 1 ? '' : 's';

  async function doSend() {
    setSending(true); setError(null);
    try {
      const res = await sendPaymentReminders(scope, message.trim());
      setSentInfo(
        `Sent to ${res.total_recipients} resident(s)` +
          (res.skipped_no_main_user ? `, skipped ${res.skipped_no_main_user} unit(s) with no Main user` : '') + '.',
      );
      setConfirm(false);
      await load(scope);
    } catch (e) { setError(extractApiError(e).message); }
    finally { setSending(false); }
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>{t('rem.title')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('rem.subtitle')}</Typography>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {sentInfo ? <Alert severity="success" onClose={() => setSentInfo(null)}>{sentInfo}</Alert> : null}

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Tile label={t('rem.tileUnitsOwe')} value={data ? String(data.units_owing) : '—'} />
        <Tile label={t('rem.tileTotalOutstanding')} value={data ? `Rp ${formatRupiah(data.total_outstanding_cents)}` : '—'} />
        <Tile label={t('rem.tileLastSent')} value={data ? formatDateTime(data.last_sent_at) : '—'} />
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>{t('rem.recipientsLabel')}</Typography>
              <ToggleButtonGroup exclusive size="small" value={scope} aria-label="Recipient scope" onChange={(_, v) => { if (v) setScope(v); }}>
                <ToggleButton value="owing">{t('rem.scopeOwing')}</ToggleButton>
                <ToggleButton value="all">{t('rem.scopeAll')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TextField
              label={t('rem.messageField')} multiline minRows={3} fullWidth value={message}
              onChange={(e) => setMessage(e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText={t('rem.messageHelper')}
            />

            {loading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : recipients.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {scope === 'owing' ? t('rem.emptyOwing') : t('rem.emptyAll')}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>{t('inv.colUnit')}</TableCell><TableCell>{t('res.mainUser')}</TableCell>
                    <TableCell align="right">{t('rem.colOutstanding')}</TableCell><TableCell>{t('rem.colDue')}</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {recipients.map((rr) => (
                      <TableRow key={rr.unit_id} hover>
                        <TableCell>{rr.unit_label}</TableCell>
                        <TableCell>
                          {rr.main_user_name ?? <Typography component="span" variant="body2" color="text.disabled">{t('rem.noMainUser')}</Typography>}
                        </TableCell>
                        <TableCell align="right">{rr.outstanding_cents > 0 ? `Rp ${formatRupiah(rr.outstanding_cents)}` : '—'}</TableCell>
                        <TableCell>{rr.earliest_due ? formatDate(rr.earliest_due) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box>
              <Button
                variant="contained" startIcon={<SendIcon />}
                disabled={loading || sendable.length === 0 || message.trim().length === 0}
                onClick={() => setConfirm(true)}
              >
                {t('rem.sendBtn', { count: sendable.length, plural })}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle>{t('rem.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('rem.confirmText', { count: sendable.length, plural, scope: t(scope === 'owing' ? 'rem.scopeOwingLabel' : 'rem.scopeAllLabel') })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)} disabled={sending}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={doSend} disabled={sending}>{sending ? t('rem.sending') : t('rem.send')}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <Card variant="outlined" sx={{ minWidth: 180, flex: '1 1 180px' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}
